/*
 * The composer's reader of foreign HTML: what a paste from another application becomes once
 * `classifyClipboard` (`../clipboardKind.ts`) has called it HTML. Word's lists are
 * rebuilt, inline styles become the marks they stand for, and an image the dialect would refuse
 * keeps only its words; everything else is the schema's own `parseDOM` rules.
 */

import { DOMParser, type Node } from "prosemirror-model"

import type { Dialect } from "../dialect/index.ts"
import { BOLD_WEIGHT } from "../dialect/marks.ts"

/**
 * Foreign HTML as a document of one dialect. Everything with no `parseDOM` rule — Word's `<o:p>`,
 * `mso-` noise, style-only spans, comments, scripts — matches nothing and dissolves into its text,
 * and an `<img>` whose source that dialect refuses keeps its alt text.
 */
export function documentFromHtml(dialect: Dialect, html: string, doc: Document = document): Node {
	// An inactive document: nothing in it is fetched, no script runs, and `<style>`, `<script>` and
	// `<title>` are dropped by ProseMirror's own parser before a rule is ever consulted.
	const parsed = doc.implementation.createHTMLDocument("clipboard")
	parsed.body.innerHTML = html
	rebuildWordLists(parsed.body)
	readInlineStyles(parsed.body)
	replaceRefusedImages(parsed.body, dialect.allowedHref)
	return DOMParser.fromSchema(dialect.schema).parse(parsed.body)
}

/**
 * Word does not write lists. It writes ordinary paragraphs that carry `mso-list` in their style,
 * and draws the bullet or the number itself into a marker span the browser is meant to hide. Left
 * alone, a pasted list becomes paragraphs that begin with a literal "·" and five spaces — which is
 * not a list any more and cannot be continued as one.
 *
 * So the paragraphs are read back as what they were: the marker says whether the list is a bullet
 * or a number and is then dropped, `level<n>` says how deep the item sits, and a run of consecutive
 * items becomes one list. Everything else Word sends is left to the `parseDOM` rules.
 */
const WORD_LIST_STYLE = /mso-list\s*:/i
const WORD_LIST_LEVEL = /level(\d+)/i
/** A marker that begins with digits is a number; "·", "o", "§" and their kin are bullets. */
const NUMBERED_MARKER = /^\s*[0-9]+[.)]/
/** The families Word draws its bullets in, for the versions that write no conditional comment. */
const MARKER_FONT = /font-family:[^;]*(symbol|wingdings|courier new)/i

interface WordListItem {
	level: number
	ordered: boolean
}

/** What a paragraph says about itself, or null when it is not one of Word's list paragraphs. */
function wordListItem(node: Element): WordListItem | null {
	if (node.tagName !== "P") return null
	const style = node.getAttribute("style") ?? ""
	const className = node.getAttribute("class") ?? ""
	if (!WORD_LIST_STYLE.test(style) && !className.startsWith("MsoListParagraph")) return null
	const level = Number(WORD_LIST_LEVEL.exec(style)?.[1] ?? "1")
	return { level: Number.isFinite(level) && level > 0 ? level : 1, ordered: false }
}

/**
 * Takes the drawn bullet or number out of one item and answers with its text. It is the whole run
 * up to the `[endif]` of Word's conditional comment; older versions write no comment and only the
 * span in the bullet font, which is removed instead.
 */
function takeMarker(item: Element): string {
	const nodes = [...item.childNodes]
	const endif = nodes.findIndex(
		(node) => node.nodeType === node.COMMENT_NODE && /\[endif\]/i.test(node.nodeValue ?? ""),
	)
	let marker = ""
	if (endif >= 0) {
		for (const node of nodes.slice(0, endif + 1)) {
			// The conditional comments carry text of their own ("[if !supportLists]"); what the
			// marker *is* stands between them.
			if (node.nodeType !== node.COMMENT_NODE) marker += node.textContent ?? ""
			node.remove()
		}
	} else {
		const first = nodes.find((node) => node.nodeType === node.ELEMENT_NODE)
		if (first instanceof HTMLElement && MARKER_FONT.test(first.getAttribute("style") ?? "")) {
			marker = first.textContent ?? ""
			first.remove()
		}
	}
	// What the marker span left behind is the gap Word drew between the bullet and the words.
	const text = item.firstChild
	if (text?.nodeType === text?.TEXT_NODE && text !== null) {
		text.nodeValue = (text.nodeValue ?? "").replace(/^[\s\u00a0]+/, "")
	}
	return marker
}

function rebuildWordLists(root: HTMLElement): void {
	const doc = root.ownerDocument
	// The lists still open, innermost last: a `level2` item after a `level1` one becomes a list
	// inside that item, and anything that is not a list item at all closes them all.
	let open: { level: number; list: HTMLElement }[] = []
	for (const child of [...root.children]) {
		const info = wordListItem(child)
		if (info === null) {
			const blank = child.tagName === "P" && (child.textContent ?? "").trim() === ""
			if (!blank) open = []
			continue
		}
		const ordered = NUMBERED_MARKER.test(takeMarker(child))
		const item = doc.createElement("li")
		item.append(...child.childNodes)
		let innermost = open.at(-1)
		while (innermost !== undefined && innermost.level > info.level) {
			open.pop()
			innermost = open.at(-1)
		}
		if (innermost === undefined || innermost.level < info.level) {
			const list = doc.createElement(ordered ? "ol" : "ul")
			const deeperThan = innermost?.list.lastElementChild
			// The first item of a list stands where its paragraph stood; a nested list goes inside
			// the item it belongs to.
			if (deeperThan === null || deeperThan === undefined) child.replaceWith(list)
			else deeperThan.append(list)
			innermost = { level: info.level, list }
			open.push(innermost)
		}
		innermost.list.append(item)
		child.remove()
	}
}

/**
 * The bold, italic and strike-through a clipboard wrote as inline styles, read as the elements they
 * stand for before any `parseDOM` rule runs.
 *
 * The page's content-security policy allows no inline style (`style-src` with a nonce and no
 * `'unsafe-inline'`), and under it Chromium leaves `element.style` empty for HTML parsed from a
 * clipboard. A rule that reads a style therefore never matches in the product — only in a test,
 * whose DOM ignores the policy. Google Docs writes every run as a styled span inside one
 * `<b style="font-weight:normal">`, so a paste from it came out all bold, with its own bold and
 * italic lost. The attribute itself is a string the policy does not touch: it is read
 * here, and what it says becomes `<strong>`, `<em>` or `<s>` around the element's children — or,
 * for a bold element that says it is not bold, its children alone.
 */
function readInlineStyles(root: HTMLElement): void {
	const doc = root.ownerDocument
	for (const element of [...root.querySelectorAll("[style]")]) {
		const style = declarations(element.getAttribute("style") ?? "")
		const weight = style.get("font-weight")
		const bold = weight === undefined ? null : BOLD_WEIGHT.test(weight)
		if ((element.tagName === "B" || element.tagName === "STRONG") && bold === false) {
			element.replaceWith(...element.childNodes)
			continue
		}
		// A block's weight or slant is the page's typography and not emphasis somebody chose.
		// Emphasis is written on the runs inside the block — which is where Google Docs and Word
		// put it — so only an inline element's style becomes a mark.
		if (BLOCK_TAGS.has(element.tagName)) continue
		const wrap = (tag: string): void => {
			const inner = doc.createElement(tag)
			inner.append(...element.childNodes)
			element.append(inner)
		}
		if (bold === true && element.tagName !== "B" && element.tagName !== "STRONG") wrap("strong")
		if (style.get("font-style") === "italic") wrap("em")
		const decoration = `${style.get("text-decoration") ?? ""} ${style.get("text-decoration-line") ?? ""}`
		if (decoration.includes("line-through")) wrap("s")
	}
}

/**
 * The elements whose style is a page's typography rather than emphasis: a browser copying a web page
 * writes every computed style inline, so a heading arrives at 600 and a lead paragraph at 500.
 */
const BLOCK_TAGS = new Set([
	"P",
	"DIV",
	"H1",
	"H2",
	"H3",
	"H4",
	"H5",
	"H6",
	"LI",
	"UL",
	"OL",
	"DL",
	"DT",
	"DD",
	"TABLE",
	"THEAD",
	"TBODY",
	"TR",
	"TD",
	"TH",
	"BLOCKQUOTE",
	"PRE",
	"SECTION",
	"ARTICLE",
])

/** An inline style's declarations by property, lower-cased and without `!important`. */
function declarations(style: string): Map<string, string> {
	const out = new Map<string, string>()
	for (const part of style.split(";")) {
		const colon = part.indexOf(":")
		if (colon < 0) continue
		const name = part.slice(0, colon).trim().toLowerCase()
		const value = part
			.slice(colon + 1)
			.replace(/!important/i, "")
			.trim()
			.toLowerCase()
		if (name !== "") out.set(name, value)
	}
	return out
}

/**
 * An image whose source this product would not link is not silently dropped: it leaves its alt text
 * behind, which is the only part of it a reader could have used anyway.
 */
function replaceRefusedImages(root: HTMLElement, allowedHref: (dest: string) => boolean): void {
	for (const img of [...root.querySelectorAll("img")]) {
		if (allowedHref(img.getAttribute("src") ?? "")) continue
		const alt = img.getAttribute("alt") ?? ""
		img.replaceWith(root.ownerDocument.createTextNode(alt))
	}
}
